import { Box, Button, Group, Heading, Text } from '@optiaxiom/react';
import { IconKey } from '@optiaxiom/icons';
import styles from './ApiKeyEmptyState.module.css';

interface ApiKeyEmptyStateProps {
  onGenerate?: () => void;
  isGenerating?: boolean;
  /** Set when the last generate attempt failed, to surface a retry message. */
  error?: boolean;
}

/**
 * Shown when the developer has no API key yet. The "Generate API key" action
 * POSTs to rivendell and the plaintext key is surfaced once in a modal by the
 * page; here we just drive the button state and a status line beneath it.
 */
export function ApiKeyEmptyState({
  onGenerate,
  isGenerating = false,
  error = false,
}: ApiKeyEmptyStateProps) {
  return (
    <Group alignItems="center" flexDirection={'column'} className={styles.container}>
      <Box className={styles.iconTile}>
        <IconKey size={30} />
      </Box>
      <Heading level="2" fontWeight="700" className={styles.title}>
        You don&rsquo;t have an API key yet
      </Heading>
      <Text color="fg.secondary" className={styles.body}>
        Generate a key to use with the OCP CLI. The key is required to use OCP features with the CLI.
        For security, the full key is shown <strong>once</strong>{' '} at generation — copy it somewhere safe.
      </Text>
      {/* Pass the icon via `addonBefore` as a plain IconKey rather than the `icon`
          prop: `icon` renders an inverse ButtonIcon, which on appearance="primary"
          is a dark chip (very visible at lg). addonBefore wraps a plain icon — no
          chip — and ButtonLabel's own margin keeps the gap. */}
      <Button
        appearance="primary"
        size="lg"
        addonBefore={<IconKey />}
        onClick={onGenerate}
        loading={isGenerating}
        disabled={isGenerating}
      >
        {isGenerating ? 'Generating…' : 'Generate API key'}
      </Button>
      {isGenerating && (
        <Text color="fg.secondary" className={styles.status}>
          Generating your key and registering it with OCP…
        </Text>
      )}
      {error && !isGenerating && (
        <Text color="fg.error" className={styles.status}>
          Something went wrong generating your key. Please try again.
        </Text>
      )}
    </Group>
  );
}
